import tv4 from "tv4";

import type { JsonSchemaDefinition } from "./schemaFormModel";

const COMBINATION_ERRORS = [
	tv4.errorCodes.ANY_OF_MISSING,
	tv4.errorCodes.ONE_OF_MISSING
];
const VALIDATION_TYPES = {
	date: "string",
	datetime: "string",
	datapoint: "number",
	entity: "number"
};

export interface JsonSchemaValidationResult {
	valid: boolean;
	fieldErrors: Record<string, string>;
	messages: string[];
}

export function validateJsonSchemaModel(
	model: unknown,
	schema?: JsonSchemaDefinition
): JsonSchemaValidationResult {
	if(!schema) {
		return {
			valid: true,
			fieldErrors: {},
			messages: []
		};
	}

	const result = tv4.validateMultiple(
		normalizeModel(model),
		buildJsonSchemaValidationSchema(schema)
	);
	const errors = result.errors.flatMap(getActionableErrors);
	const fieldErrors = errors.reduce((items, error) => {
		const path = getErrorDataPath(error);
		if(path) {
			items[path] = getErrorMessage(error, schema, path);
		}

		return items;
	}, {} as Record<string, string>);
	const messages = errors.reduce((items, error) => {
		const message = getErrorMessage(error, schema, getErrorDataPath(error));
		if(message && !items.includes(message)) {
			items.push(message);
		}

		return items;
	}, [] as string[]);

	return {
		valid: result.valid,
		fieldErrors,
		messages
	};
}

export function buildJsonSchemaValidationSchema(
	schema: JsonSchemaDefinition
): JsonSchemaDefinition {
	const validationSchema = {
		...schema,
		type: normalizeSchemaType(schema.type)
	};

	if(schema.properties) {
		validationSchema.properties = Object.entries(schema.properties)
			.reduce((properties, [key, propertySchema]) => {
				properties[key] = buildJsonSchemaValidationSchema(propertySchema);
				return properties;
			}, {} as Record<string, JsonSchemaDefinition>);
	}
	if(schema.items) {
		validationSchema.items = buildJsonSchemaValidationSchema(schema.items);
	}
	for(const keyword of ["allOf", "anyOf", "oneOf"] as const) {
		const branches = schema[keyword];
		if(Array.isArray(branches)) {
			validationSchema[keyword] = branches.map(buildJsonSchemaValidationSchema);
		}
	}
	if(schema.not) {
		validationSchema.not = buildJsonSchemaValidationSchema(schema.not);
	}

	return validationSchema;
}

function normalizeSchemaType(
	type: JsonSchemaDefinition["type"]
): JsonSchemaDefinition["type"] {
	if(Array.isArray(type)) {
		return type.map(normalizeSingleSchemaType);
	}

	return type ? normalizeSingleSchemaType(type) : type;
}

function normalizeSingleSchemaType(type: string): string {
	return VALIDATION_TYPES[type.toLowerCase()] ?? type;
}

function normalizeModel(model: unknown): unknown {
	if(model === null) {
		return undefined;
	}

	if(Array.isArray(model)) {
		return model.map(normalizeModel);
	}

	if(model && typeof model === "object") {
		return Object.entries(model).reduce((items, [key, value]) => {
			const normalizedValue = normalizeModel(value);
			if(normalizedValue !== undefined) {
				items[key] = normalizedValue;
			}

			return items;
		}, {} as Record<string, unknown>);
	}

	return typeof model === "string" && model.trim() === "" ? undefined : model;
}

function getActionableErrors(error): unknown[] {
	if(!COMBINATION_ERRORS.includes(error.code) || !error.subErrors?.length) {
		return [error];
	}

	const candidateErrors = error.subErrors.filter(subError => subError.code !== tv4.errorCodes.NOT_PASSED);
	return candidateErrors.length === 1
		? candidateErrors.flatMap(getActionableErrors)
		: [error];
}

function getErrorDataPath(error): string {
	if(error.code !== tv4.errorCodes.OBJECT_REQUIRED) {
		return error.dataPath;
	}

	const requiredProperty = error.message.match(/^Missing required property: (.+)$/)?.[1];
	return requiredProperty
		? `${error.dataPath}/${escapeJsonPointerPart(requiredProperty)}`
		: error.dataPath;
}

function getErrorMessage(error, schema: JsonSchemaDefinition, path: string): string {
	const configuredMessage = findConfiguredValidationMessage(error, schema);
	if(configuredMessage) {
		return configuredMessage;
	}

	const title = getSchemaTitle(schema, path);
	if(error.code === tv4.errorCodes.OBJECT_REQUIRED) {
		const pathParts = parseJsonPointer(path);
		const propertyName = pathParts[pathParts.length - 1];
		if(title || propertyName) {
			return `${title ?? propertyName} is required.`;
		}
	}

	return title ? `${title}: ${error.message}` : error.message;
}

function findConfiguredValidationMessage(error, schema: JsonSchemaDefinition): string | undefined {
	return findSchemaPathValidationMessage(error.schemaPath, error.code, schema)
		?? error.subErrors?.reduce(
			(message, subError) => message ?? findConfiguredValidationMessage(subError, schema),
			undefined
		)
		?? getValidationMessage(schema, error.code);
}

function findSchemaPathValidationMessage(
	path: string,
	code: number,
	schema: JsonSchemaDefinition
): string | undefined {
	return parseJsonPointer(path).reduceRight((message, _, index, parts) => {
		if(message) {
			return message;
		}

		const schemaNode = parts
			.slice(0, index + 1)
			.reduce((node, part) => node?.[part], schema as unknown);
		return getValidationMessage(schemaNode, code);
	}, undefined as string | undefined);
}

function getValidationMessage(schemaNode, code: number): string | undefined {
	const validationMessage = schemaNode?.validationMessage ?? schemaNode?.["x-validationMessage"];

	if(typeof validationMessage === "string") {
		return validationMessage;
	}

	return validationMessage?.[code] ?? validationMessage?.default;
}

function getSchemaTitle(schema: JsonSchemaDefinition, path: string): string | undefined {
	return parseJsonPointer(path).reduce((node, part) => {
		if(!node) {
			return undefined;
		}

		const isArray = Array.isArray(node.type)
			? node.type.includes("array")
			: node.type === "array";
		return isArray
			? node.items
			: node.properties?.[part];
	}, schema)?.title as string | undefined;
}

export function jsonPointerToSchemaFormPath(path: string): string {
	return parseJsonPointer(path).join(".");
}

function parseJsonPointer(path: string): string[] {
	return path
		?.split("/")
		.slice(1)
		.map(part => part.replace(/~1/g, "/").replace(/~0/g, "~")) ?? [];
}

function escapeJsonPointerPart(part: string): string {
	return part.replace(/~/g, "~0").replace(/\//g, "~1");
}
